import type { CalloutAttachment, DrawingDocument, Point2D } from '../model';
import type { EditorSelection } from './editor';

/** Callouts reuse positioned rich text; metadata notes and arrow anchors have separate rules. */
export function calloutTargetPosition(document: DrawingDocument, target: CalloutAttachment): Point2D | undefined {
  const coordinates = document.depiction.coordinateSet.coordinates;
  if (target.kind === 'atom') return coordinates[target.atomId];
  if (target.kind === 'atom-set') {
    const points = target.atomIds.map((id) => coordinates[id]);
    if (!points.length || points.some((point) => !point)) return undefined;
    return points.reduce<Point2D>(
      (sum, point) => ({ x: sum.x + point!.x / points.length, y: sum.y + point!.y / points.length }),
      { x: 0, y: 0 }
    );
  }
  if (target.kind === 'bond') {
    const bond = document.structure.graph.edges.find((edge) => edge.id === target.bondId);
    const a = bond && coordinates[bond.source];
    const b = bond && coordinates[bond.target];
    return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : undefined;
  }
  const annotation = document.annotations.find((item) => item.id === target.annotationId);
  // Ordinary shapes only: reaction zones/arrows retain their own ownership semantics.
  if (annotation?.kind !== 'shape' || annotation.callout) return undefined;
  if (annotation.bounds)
    return {
      x: annotation.bounds.x + annotation.bounds.width / 2,
      y: annotation.bounds.y + annotation.bounds.height / 2
    };
  const points = annotation.points;
  return points?.length
    ? {
        x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
        y: points.reduce((sum, point) => sum + point.y, 0) / points.length
      }
    : undefined;
}

export function calloutSelection(document: DrawingDocument, selection: EditorSelection) {
  const selected = document.annotations.filter((item) => selection.annotationIds?.includes(item.id));
  const texts = selected.filter((item) => item.kind === 'text' || (item.kind === 'shape' && item.callout));
  const shapes = selected.filter((item) => item.kind === 'shape' && !item.callout);
  let target: CalloutAttachment | undefined;
  if (selection.atomIds.length === 1 && selection.bondIds.length === 0 && shapes.length === 0) {
    target = { kind: 'atom', atomId: selection.atomIds[0]! };
  } else if (selection.bondIds.length === 1 && shapes.length === 0) {
    target = { kind: 'bond', bondId: selection.bondIds[0]! };
  } else if (selection.atomIds.length === 0 && selection.bondIds.length === 0 && shapes.length === 1) {
    target = { kind: 'annotation', annotationId: shapes[0]!.id };
  } else if (selection.atomIds.length > 1 && shapes.length === 0) {
    target = { kind: 'atom-set', atomIds: [selection.atomIds[0]!, ...selection.atomIds.slice(1)] };
  }
  return {
    ids: texts.map((text) => text.id),
    target: target && calloutTargetPosition(document, target) ? target : undefined,
    canDetach: texts.some((text) =>
      text.kind === 'text'
        ? text.attachment !== undefined
        : text.kind === 'shape' && text.callout?.attachment !== undefined
    )
  };
}

export function setCalloutAttachment(
  document: DrawingDocument,
  ids: readonly string[],
  target?: CalloutAttachment
): DrawingDocument {
  if (target && !calloutTargetPosition(document, target)) return document;
  let changed = false;
  const annotations = document.annotations.map((annotation) => {
    if (!ids.includes(annotation.id)) return annotation;
    if (annotation.kind === 'shape' && annotation.callout) {
      const { attachment: _attachment, ...bubble } = annotation.callout;
      if (JSON.stringify(annotation.callout.attachment) === JSON.stringify(target)) return annotation;
      changed = true;
      return { ...annotation, callout: { ...bubble, ...(target ? { attachment: target } : {}) } };
    }
    if (annotation.kind !== 'text') return annotation;
    if (JSON.stringify(annotation.attachment) === JSON.stringify(target)) return annotation;
    changed = true;
    const { attachment: _attachment, ...text } = annotation;
    return target ? { ...text, attachment: target } : text;
  });
  return changed ? { ...document, annotations } : document;
}

/** Resolve dependent movement/deletion inside the same document transaction as its target.
 * An explicitly moved callout already received the selection offset and must not move twice.
 */
export function reconcileCallouts(before: DrawingDocument, after: DrawingDocument): DrawingDocument {
  let changed = false;
  const previous = new Map(before.annotations.map((annotation) => [annotation.id, annotation]));
  const annotations = after.annotations.map((annotation) => {
    if (annotation.kind === 'shape' && annotation.callout?.attachment) {
      const bubble = annotation.callout;
      const to = calloutTargetPosition(after, bubble.attachment!);
      if (!to) {
        changed = true;
        const { attachment: _attachment, ...detached } = bubble;
        return { ...annotation, callout: detached };
      }
      const old = previous.get(annotation.id);
      if (
        old?.kind !== 'shape' ||
        !old.callout ||
        JSON.stringify(old.callout.attachment) !== JSON.stringify(bubble.attachment)
      )
        return annotation;
      const from = calloutTargetPosition(before, bubble.attachment!);
      if (!from || (from.x === to.x && from.y === to.y)) return annotation;
      const dx = to.x - from.x,
        dy = to.y - from.y;
      const bodyUnchanged = JSON.stringify(old.bounds) === JSON.stringify(annotation.bounds);
      changed = true;
      return {
        ...annotation,
        ...(bodyUnchanged && annotation.bounds
          ? { bounds: { ...annotation.bounds, x: annotation.bounds.x + dx, y: annotation.bounds.y + dy } }
          : {}),
        callout: { ...bubble, tip: { x: bubble.tip.x + dx, y: bubble.tip.y + dy } }
      };
    }
    if (annotation.kind !== 'text' || !annotation.attachment) return annotation;
    const to = calloutTargetPosition(after, annotation.attachment);
    if (!to) {
      changed = true;
      const { attachment: _attachment, ...detached } = annotation;
      return detached;
    }
    const old = previous.get(annotation.id);
    if (old?.kind !== 'text' || JSON.stringify(old.attachment) !== JSON.stringify(annotation.attachment))
      return annotation;
    if (old.position.x !== annotation.position.x || old.position.y !== annotation.position.y) return annotation;
    const from = calloutTargetPosition(before, annotation.attachment);
    if (!from || (from.x === to.x && from.y === to.y)) return annotation;
    changed = true;
    return {
      ...annotation,
      position: { x: annotation.position.x + to.x - from.x, y: annotation.position.y + to.y - from.y }
    };
  });
  return changed ? { ...after, annotations } : after;
}
